import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import QRCode from "react-qr-code";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { postOrQueue } from "../lib/offlineQueue";
import { useAuth } from "../context/AuthContext";
import PhotoInput from "../components/PhotoInput";
import StatusChip from "../components/StatusChip";
import { ArrowLeft, Star, UserPlus } from "lucide-react";

const dt = (s) => (s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata" }) : "—");
const FUELS = ["E", "1/4", "1/2", "3/4", "F"];
const QUALITY_COLORS = { Hot: "bg-[#BB162B] border-[#BB162B] text-white", Warm: "bg-[#BF9B30] border-[#BF9B30] text-white", Cold: "bg-sky-600 border-sky-600 text-white", Lost: "bg-slate-800 border-slate-800 text-white", Junk: "bg-slate-500 border-slate-500 text-white" };
const inputCls = "mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E2240]";

function StartTrip({ bookingId }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [km, setKm] = useState("");
  const [fuel, setFuel] = useState("");
  const [odoPhoto, setOdoPhoto] = useState(null);
  const [dlPhoto, setDlPhoto] = useState(null);
  const [booking, setBooking] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/bookings", { params: { status: "blocked" } }).then((r) => setBooking(r.data.find((b) => b.id === bookingId) || null)).catch(() => {}); }, [bookingId]);
  const isHome = booking?.type === "home";
  const dlNeeded = !booking || booking.type === "showroom";

  const submit = async () => {
    if (!km || !fuel || !odoPhoto || (dlNeeded && !dlPhoto)) { toast.error(dlNeeded ? "Opening km, fuel level, odometer photo and DL photo are all mandatory" : "Opening km, fuel level and odometer photo are mandatory"); return; }
    setBusy(true);
    try {
      const res = await postOrQueue("/trips/start", { booking_id: bookingId, opening_km: parseInt(km), fuel_level: fuel, odometer_photo: odoPhoto, dl_photo: dlPhoto }, "Trip start");
      if (res.queued) {
        toast.info("No network — trip start saved offline. Gate pass will be issued once it syncs.");
        nav("/trips", { replace: true });
        return;
      }
      toast.success("Trip started — gate pass issued");
      nav(`/trip/${res.data.id}`, { replace: true });
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <div className="mt-4 space-y-4">
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Opening odometer (km)</label>
        <input data-testid="opening-km-input" type="number" value={km} onChange={(e) => setKm(e.target.value)} placeholder="12345" className={inputCls} />
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Fuel level</label>
        <div className="mt-1 flex gap-2">
          {FUELS.map((f) => (
            <button key={f} data-testid={`fuel-level-${f.replace("/", "-")}`} onClick={() => setFuel(f)}
              className={`flex-1 rounded-xl border py-3 text-sm font-bold ${fuel === f ? "border-[#0E2240] bg-[#0E2240] text-white" : "border-slate-200 bg-white text-slate-600"}`}>{f}</button>
          ))}
        </div>
      </div>
      <PhotoInput label="odometer photo" value={odoPhoto} onChange={setOdoPhoto} testId="odometer-photo-btn" />
      {isHome ? (
        <div data-testid="home-dl-deferred-note" className="rounded-xl border border-sky-200 bg-sky-50 p-3 text-xs text-sky-800">
          <b>Home TD:</b> the customer's driving licence is collected at their place — upload it from the trip screen before gate-in. The SM is alerted if it's still missing at the gate.
        </div>
      ) : (
        <PhotoInput label="driving licence" value={dlPhoto} onChange={setDlPhoto} testId="dl-photo-btn" allowGallery={user.role === "tdc"} />
      )}
      <button data-testid="start-trip-submit-btn" disabled={busy} onClick={submit}
        className="w-full rounded-xl bg-[#BB162B] py-3.5 text-sm font-bold text-white active:scale-[0.98] transition-transform duration-150 disabled:opacity-60">
        {busy ? "Starting…" : "Start Trip & Issue Gate Pass"}
      </button>
    </div>
  );
}

function CloseTrip({ trip, onDone }) {
  const [km, setKm] = useState("");
  const [photo, setPhoto] = useState(null);
  const [stars, setStars] = useState({ experience: 0, vehicle: 0, kec: 0 });
  const [quality, setQuality] = useState("");
  const [qualityType, setQualityType] = useState("");
  const [opts, setOpts] = useState({ qualities: ["Hot", "Warm", "Cold", "Lost", "Junk"], types: {} });
  const [busy, setBusy] = useState(false);
  const isUtility = trip.type === "utility";
  useEffect(() => { if (!isUtility) api.get("/trips/lead-quality-options").then((r) => setOpts(r.data)).catch(() => {}); }, [isUtility]);

  const submit = async () => {
    if (!km || !photo) { toast.error("Closing km and odometer photo are mandatory"); return; }
    if (!isUtility && !quality) { toast.error("Pick the Lead Quality — Salesforce needs it to score this enquiry"); return; }
    if (!isUtility && !qualityType) { toast.error(`Pick the ${quality} reason (Quality Type)`); return; }
    setBusy(true);
    try {
      const res = await postOrQueue(`/trips/${trip.id}/close`, {
        closing_km: parseInt(km), odometer_photo: photo,
        feedback: Object.values(stars).some((v) => v) ? stars : null,
        lead_quality: isUtility ? null : quality, quality_type: isUtility ? null : qualityType,
      }, `Trip close · ${trip.reg_no}`);
      if (res.queued) {
        toast.info("No network — trip close saved offline and will sync automatically.");
        onDone();
        return;
      }
      toast.success(`Trip closed · ${res.data.km_driven} km driven${res.data.deviation_flag ? " · deviation flagged" : ""}`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <div className="mt-4 space-y-4 rounded-2xl border border-slate-200 bg-white p-4">
      <h3 className="font-heading font-bold text-[#0E2240]">Close Trip</h3>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Closing odometer (km)</label>
        <input data-testid="closing-km-input" type="number" value={km} onChange={(e) => setKm(e.target.value)} placeholder={`≥ ${trip.opening_km}`} className={inputCls} />
      </div>
      <PhotoInput label="closing odometer photo" value={photo} onChange={setPhoto} testId="closing-odo-photo-btn" />
      {!isUtility && (
        <div data-testid="lead-quality-step" className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Lead quality <span className="text-[#BB162B]">*</span></label>
          <p className="mt-0.5 text-[11px] text-slate-500">Sent to Salesforce with the TD Completion — drives lead scoring.</p>
          <div className="mt-2 grid grid-cols-5 gap-1.5">
            {opts.qualities.map((q) => (
              <button key={q} type="button" data-testid={`lead-quality-${q.toLowerCase()}`} onClick={() => { setQuality(q); setQualityType(""); }}
                className={`rounded-xl border py-2.5 text-xs font-bold transition-colors duration-150 ${quality === q ? QUALITY_COLORS[q] : "border-slate-200 bg-white text-slate-600"}`}>{q}</button>
            ))}
          </div>
          {quality && (
            <div data-testid="quality-type-step" className="mt-3">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{quality} reason <span className="text-[#BB162B]">*</span></label>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {(opts.types[quality] || []).map((t) => (
                  <button key={t} type="button" data-testid={`quality-type-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => setQualityType(t)}
                    className={`rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-colors duration-150 ${qualityType === t ? "border-[#0E2240] bg-[#0E2240] text-white" : "border-slate-200 bg-white text-slate-600"}`}>{t}</button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Customer feedback (optional)</label>
        {[["experience", "Test drive experience"], ["vehicle", "Vehicle impression"], ["kec", "KEC assistance"]].map(([k, l]) => (
          <div key={k} className="mt-2 flex items-center justify-between">
            <span className="text-xs text-slate-600">{l}</span>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} data-testid={`star-${k}-${n}`} onClick={() => setStars((s) => ({ ...s, [k]: n }))}>
                  <Star size={20} className={n <= stars[k] ? "fill-[#BF9B30] text-[#BF9B30]" : "text-slate-300"} />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <button data-testid="close-trip-submit-btn" disabled={busy} onClick={submit}
        className="w-full rounded-xl bg-[#0E2240] py-3.5 text-sm font-bold text-white active:scale-[0.98] transition-transform duration-150 disabled:opacity-60">
        {busy ? "Closing…" : "Close Trip"}
      </button>
    </div>
  );
}

function DLUpload({ trip, onDone }) {
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!photo) { toast.error("Take the DL photo first"); return; }
    setBusy(true);
    try { await api.post(`/trips/${trip.id}/dl`, { dl_photo: photo }); toast.success("Driving licence saved"); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="dl-upload-card" className="mt-4 rounded-2xl border border-amber-300 bg-amber-50 p-4">
      <h3 className="text-sm font-bold text-amber-800">Customer driving licence pending</h3>
      <p className="mt-0.5 text-[11px] text-amber-700">Collect it at the customer's place and upload before gate-in.</p>
      <PhotoInput label="driving licence" value={photo} onChange={setPhoto} testId="trip-dl-photo-btn" />
      <button data-testid="trip-dl-save-btn" disabled={busy} onClick={save} className="mt-2 w-full rounded-xl bg-amber-600 py-2.5 text-sm font-bold text-white disabled:opacity-60">{busy ? "Saving…" : "Save DL"}</button>
    </div>
  );
}

const ADD_STATUS = { pending_sm_approval: ["Awaiting SM", "bg-amber-100 text-amber-700"], approved: ["Approved", "bg-emerald-100 text-emerald-700"], rejected: ["Rejected", "bg-red-100 text-red-700"], completed: ["Counted", "bg-slate-100 text-slate-600"], expired: ["Not counted", "bg-slate-100 text-slate-500"] };

function AddDrive({ trip, onDone }) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [enq, setEnq] = useState(null);
  const [err, setErr] = useState("");
  const [dl, setDl] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const lookup = async () => {
    setErr(""); setEnq(null);
    try { const { data } = await api.get("/salesforce/lookup", { params: { phone } }); setEnq(data); }
    catch (e) { setErr(errMsg(e)); }
  };
  const [dlFor, setDlFor] = useState(null);
  const [dlLater, setDlLater] = useState(null);
  const saveDl = async () => {
    if (!dlLater) { toast.error("Take the DL photo first"); return; }
    try { await api.post(`/trip-additions/${dlFor}/dl`, { dl_photo: dlLater }); toast.success("Driving licence saved"); setDlFor(null); setDlLater(null); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const submit = async () => {
    if (!enq) { toast.error("Look up the enquiry first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/trips/${trip.id}/additions`, { enquiry_id: enq.enquiry_id, phone, dl_photo: dl || null, note: note.trim() || null });
      toast.success(data.status === "approved" ? "Extra drive added" : "Sent to SM — drive counts once approved");
      setOpen(false); setPhone(""); setEnq(null); setDl(null); setNote(""); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const adds = trip.additions || [];
  return (
    <div data-testid="add-drive-card" className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-bold text-[#0E2240]">{trip.type === "transfer" ? "Test drives on the way" : trip.type === "utility" ? "On-spot test drives at the venue" : "Extra drives on this outing"}</h3>
          <p className="text-[11px] text-slate-500">{trip.type === "transfer" ? "Walk-in or referral while you drive this transfer? Look up their Salesforce enquiry — your SM approves in one tap, then DL + start/end drive below. Closes automatically at the destination gate." : trip.type === "utility" ? "Walk-in at the event? Look up their Salesforce enquiry — one-tap SM approval, then DL + start/end drive below." : "Spot enquiry or referral while you're out — needs SM approval before gate-in. DL can be uploaded later, before closing."}</p>
        </div>
        <button data-testid="add-drive-btn" onClick={() => setOpen(!open)} className="flex items-center gap-1 rounded-xl bg-[#0E2240] px-3 py-2 text-xs font-bold text-white"><UserPlus size={14} /> Add a drive</button>
      </div>
      {adds.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {adds.map((a) => (
            <div key={a.id} data-testid={`addition-${a.enquiry_id}`} className="rounded-lg bg-slate-50 px-3 py-2 text-xs">
              <div className="flex items-center justify-between">
                <div><b className="text-[#0E2240]">{a.customer_name}</b> · {a.enquiry_id}{a.additional_model && <span className="ml-1 text-amber-700">· Additional model ({a.enquiry_model})</span>}{a.rejection_reason && <div className="text-[#BB162B]">{a.rejection_reason}</div>}</div>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${ADD_STATUS[a.status]?.[1] || "bg-slate-100"}`}>{ADD_STATUS[a.status]?.[0] || a.status}</span>
              </div>
              {!a.dl_photo && ["pending_sm_approval", "approved"].includes(a.status) && (
                dlFor === a.id ? (
                  <div className="mt-2">
                    <PhotoInput label="customer driving licence" value={dlLater} onChange={setDlLater} testId={`addition-dl-photo-${a.enquiry_id}`} />
                    <div className="mt-1.5 flex gap-2">
                      <button data-testid={`addition-dl-save-${a.enquiry_id}`} onClick={saveDl} className="flex-1 rounded-lg bg-amber-600 py-2 text-[11px] font-bold text-white">Save DL</button>
                      <button onClick={() => { setDlFor(null); setDlLater(null); }} className="rounded-lg border border-slate-200 px-3 text-[11px] text-slate-500">Back</button>
                    </div>
                  </div>
                ) : (
                  <button data-testid={`addition-dl-btn-${a.enquiry_id}`} onClick={() => setDlFor(a.id)} className="mt-1.5 rounded-lg border border-amber-400 bg-amber-50 px-2.5 py-1 text-[10px] font-bold text-amber-800">{["utility", "transfer"].includes(trip.type) ? "DL pending — needed to start the drive" : "DL pending — upload before closing (else flagged to SM)"}</button>
                )
              )}
            </div>
          ))}
        </div>
      )}
      {open && (
        <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
          <div className="flex gap-2">
            <input data-testid="add-drive-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Customer mobile (Salesforce)" className={inputCls} />
            <button data-testid="add-drive-lookup-btn" onClick={lookup} className="mt-1 rounded-xl border border-[#0E2240] px-3 text-xs font-bold text-[#0E2240]">Look up</button>
          </div>
          {err && <div data-testid="add-drive-lookup-error" className="rounded-lg bg-red-50 p-2 text-xs text-[#BB162B]">{err}</div>}
          {enq && (
            <div data-testid="add-drive-enquiry" className="rounded-lg bg-emerald-50 p-2.5 text-xs text-emerald-800">
              <b>{enq.customer_name}</b> · {enq.enquiry_id} · interested in {enq.model_of_interest || "—"} · {enq.enquiry_status}
              {enq.model_of_interest && enq.model_of_interest !== trip.model && <div className="mt-0.5 font-semibold text-amber-700">Additional model drive — enquiry is for {enq.model_of_interest}, driving {trip.model}</div>}
            </div>
          )}
          <PhotoInput label={["utility", "transfer"].includes(trip.type) ? "customer driving licence (optional now — required to start the drive)" : "customer driving licence (optional now — missing at close is flagged to SM)"} value={dl} onChange={setDl} testId="add-drive-dl-btn" />
          <input data-testid="add-drive-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note for SM (e.g. referral from current customer)" className={inputCls} />
          <button data-testid="add-drive-submit-btn" disabled={busy} onClick={submit} className="w-full rounded-xl bg-[#BB162B] py-2.5 text-sm font-bold text-white disabled:opacity-60">{busy ? "Sending…" : "Send to SM for approval"}</button>
        </div>
      )}
    </div>
  );
}

function Segments({ trip, onDone }) {
  const { user } = useAuth();
  const [dls, setDls] = useState({});
  const [km, setKm] = useState({});
  const [lq, setLq] = useState({});
  const [qt, setQt] = useState({});
  const [opts, setOpts] = useState({ qualities: [], types: {} });
  useEffect(() => { if (["utility", "transfer"].includes(trip.type)) api.get("/trips/lead-quality-options").then((r) => setOpts(r.data)).catch(() => {}); }, [trip.type]);
  const people = [...(trip.customers || []).map((c) => ({ ...c, source: "customer" })),
    ...(trip.additions || []).filter((a) => ["approved", "completed"].includes(a.status)).map((a) => ({ ...a, source: "addition" }))];
  const segOf = (enq) => (trip.segments || []).find((s) => s.enquiry_id === enq);
  const canAct = (p) => trip.status !== "closed" && (user.role !== "sales" || p.kec_roster_id === user.id);
  const gatedOut = !!trip.gate_out_ts;
  const start = async (p) => {
    const dl = dls[p.enquiry_id] || p.dl_photo;
    if (!dl) { toast.error("Capture this customer's driving licence first"); return; }
    try { await api.post(`/trips/${trip.id}/segments/start`, { enquiry_id: p.enquiry_id, dl_photo: dl }); toast.success(`Drive started — ${p.customer_name}`); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const end = async (p, seg) => {
    const body = { segment_km: km[p.enquiry_id] ? parseFloat(km[p.enquiry_id]) : null };
    if (seg.on_spot) {
      if (!lq[p.enquiry_id] || !qt[p.enquiry_id]) { toast.error("Pick Lead Quality and its reason to finish this on-spot drive"); return; }
      Object.assign(body, { lead_quality: lq[p.enquiry_id], quality_type: qt[p.enquiry_id] });
    }
    try { await api.post(`/trips/${trip.id}/segments/${seg.id}/end`, body); toast.success(seg.on_spot ? "Drive ended — synced to Salesforce" : `Drive ended — ${p.customer_name}`); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (people.length === 0) return null;
  return (
    <div data-testid="segments-card" className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
      <h3 className="text-sm font-bold text-[#0E2240]">{trip.type === "connected" ? `Customer drives — ${people.length}` : "Drives on this trip"}</h3>
      <p className="text-[11px] text-slate-500">One vehicle, one gate pass. Each customer's drive is recorded here{!gatedOut && " — available after gate-out"}.</p>
      <div className="mt-3 space-y-2">
        {people.map((p) => {
          const seg = segOf(p.enquiry_id);
          return (
            <div key={p.enquiry_id} data-testid={`segment-${p.enquiry_id}`} className="rounded-xl border border-slate-100 bg-slate-50 p-3 text-xs">
              <div className="flex items-center justify-between gap-2">
                <div><b className="text-[#0E2240]">{p.customer_name}</b> · KEC {p.kec_name}{p.additional_model && <span className="ml-1 text-amber-700">· Additional model</span>}</div>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${seg?.end_ts ? "bg-emerald-100 text-emerald-700" : seg ? "bg-sky-100 text-sky-700" : "bg-slate-200 text-slate-600"}`}>{seg?.end_ts ? "Done" : seg ? "Driving" : "Not started"}</span>
              </div>
              {!seg && canAct(p) && gatedOut && (
                <div className="mt-2 space-y-2">
                  {!p.dl_photo && <PhotoInput label="driving licence" value={dls[p.enquiry_id]} onChange={(b) => setDls({ ...dls, [p.enquiry_id]: b })} testId={`segment-dl-${p.enquiry_id}`} />}
                  <button data-testid={`segment-start-${p.enquiry_id}`} onClick={() => start(p)} className="w-full rounded-lg bg-[#0E2240] py-2 text-[11px] font-bold text-white">Start drive</button>
                </div>
              )}
              {seg && !seg.end_ts && canAct(p) && (
                <div className="mt-2 space-y-2">
                  <input data-testid={`segment-km-${p.enquiry_id}`} type="number" inputMode="decimal" value={km[p.enquiry_id] || ""} onChange={(e) => setKm({ ...km, [p.enquiry_id]: e.target.value })} placeholder="Segment km (optional)" className={inputCls} />
                  {seg.on_spot && (
                    <>
                      <div className="grid grid-cols-5 gap-1">
                        {opts.qualities.map((q) => <button key={q} data-testid={`segment-lq-${p.enquiry_id}-${q.toLowerCase()}`} onClick={() => { setLq({ ...lq, [p.enquiry_id]: q }); setQt({ ...qt, [p.enquiry_id]: "" }); }} className={`rounded-lg border py-1.5 text-[10px] font-bold ${lq[p.enquiry_id] === q ? QUALITY_COLORS[q] : "border-slate-200 bg-white text-slate-600"}`}>{q}</button>)}
                      </div>
                      {lq[p.enquiry_id] && <select data-testid={`segment-qt-${p.enquiry_id}`} value={qt[p.enquiry_id] || ""} onChange={(e) => setQt({ ...qt, [p.enquiry_id]: e.target.value })} className={inputCls}><option value="">Reason…</option>{(opts.types[lq[p.enquiry_id]] || []).map((t) => <option key={t} value={t}>{t}</option>)}</select>}
                    </>
                  )}
                  <button data-testid={`segment-end-${p.enquiry_id}`} onClick={() => end(p, seg)} className="w-full rounded-lg bg-emerald-600 py-2 text-[11px] font-bold text-white">End drive</button>
                </div>
              )}
              {seg?.end_ts && <div className="mt-1 text-[10px] text-slate-500">{seg.segment_km ? `${seg.segment_km} km · ` : ""}ended {new Date(seg.end_ts).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" })}{seg.synced && " · synced to Salesforce"}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AdminForceClose({ trip, onDone }) {
  const [reason, setReason] = useState("");
  const [km, setKm] = useState(trip.gate_pass?.gate_in_odometer ?? "");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!reason.trim()) { toast.error("A reason is mandatory to force-close"); return; }
    if (!window.confirm(`Force-close ${trip.reg_no}? The vehicle is released for new test drives and this is logged as an Admin exception.`)) return;
    setBusy(true);
    try {
      await api.post("/admin/force-close", { kind: "trip", id: trip.id, reason: reason.trim(), closing_km: km === "" ? null : parseInt(km) });
      toast.success(`${trip.reg_no} force-closed — vehicle released`); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="admin-force-close-panel" className="mt-4 rounded-2xl border border-red-200 bg-red-50 p-4">
      <h3 className="text-sm font-bold text-[#BB162B]">Admin Force Close</h3>
      <p className="mt-0.5 text-[11px] text-red-700">Use when a gate scan was missed and the trip can't close normally. {trip.gate_out_ts ? "" : "Gate-out is missing — "}{trip.gate_in_ts ? "" : "Gate-in is missing — "}the car is released, the drive is {trip.gate_out_ts && trip.gate_in_ts ? "counted" : "NOT counted"} and the close appears in Exceptions.</p>
      <input data-testid="force-close-km-input" type="number" value={km} onChange={(e) => setKm(e.target.value)} placeholder={`Closing km (optional, ≥ ${trip.opening_km})`} className={inputCls} />
      <input data-testid="force-close-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (mandatory) — e.g. gate-out scan not saved on server" className={inputCls} />
      <button data-testid="force-close-confirm-btn" onClick={go} disabled={busy || !reason.trim()} className="mt-3 w-full rounded-xl bg-[#BB162B] py-3 text-sm font-bold text-white disabled:opacity-40">Force Close Trip</button>
    </div>
  );
}

const TYPE_LABEL = { home: "Home TD", showroom: "Showroom", connected: "Connected drive", utility: "Utility", transfer: "Drives on transfer" };

export default function TripDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [trip, setTrip] = useState(null);
  const [ovOdo, setOvOdo] = useState("");
  const [ovReason, setOvReason] = useState("");
  const isNew = id === "new";
  const bookingId = params.get("booking");

  const load = () => { if (!isNew) api.get(`/trips/${id}`).then((r) => setTrip(r.data)).catch(() => {}); };
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [id]);

  const override = async (action) => {
    if (!ovOdo || !ovReason) { toast.error("Odometer and reason required for override"); return; }
    try {
      await api.post(`/trips/${trip.id}/gate-override`, { action, odometer: parseInt(ovOdo), reason: ovReason });
      toast.success(`Manual gate-${action} recorded`); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (isNew) {
    return (
      <div className="px-4 py-5 lg:px-8 max-w-xl">
        <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-slate-500"><ArrowLeft size={15} /> Back</button>
        <h1 className="mt-2 font-heading text-2xl font-bold text-[#0E2240]">Start Trip</h1>
        <StartTrip bookingId={bookingId} />
      </div>
    );
  }
  if (!trip) return <div className="p-8 text-slate-400">Loading…</div>;
  const gp = trip.gate_pass;
  const isTransfer = trip.type === "transfer";
  const steps = isTransfer
    ? [["Transfer Gate Out", true], ["On the Road", true], ["Destination Gate In", !!trip.gate_in_ts], ["Closed", trip.status === "closed"]]
    : [["Blocked", true], ["Trip Started", true], ["Gate Out", !!trip.gate_out_ts], ["On Test Drive", !!trip.gate_out_ts], ["Gate In", !!trip.gate_in_ts], ["Closed", trip.status === "closed"]];

  return (
    <div className="px-4 py-5 lg:px-8 max-w-xl">
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-slate-500"><ArrowLeft size={15} /> Back</button>
      <div className="mt-2 flex items-start justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#0E2240]">{trip.reg_no}</h1>
          <p className="text-sm text-slate-500">{isTransfer ? `${trip.kec_name} · ${trip.customer_name}` : `${trip.kec_name} → ${trip.customer_name} · ${TYPE_LABEL[trip.type] || trip.type}`}{trip.type === "utility" && trip.utility_purpose ? ` · ${trip.utility_purpose.replace(/_/g, " ")}` : ""}</p>
        </div>
        {isTransfer ? <span data-testid="transfer-trip-chip" className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${trip.status === "closed" ? "bg-slate-200 text-slate-700" : "bg-indigo-100 text-indigo-700"}`}>{trip.status === "closed" ? "Closed" : "On the road"}</span> : <StatusChip status={trip.status} />}
      </div>

      <div className="mt-4 flex items-center gap-0.5">
        {steps.map(([l, done], i) => (
          <div key={l} className="flex-1">
            <div className={`h-1.5 rounded-full ${done ? "bg-[#BB162B]" : "bg-slate-200"}`} />
            <div className={`mt-1 text-center text-[8px] font-bold uppercase ${done ? "text-[#BB162B]" : "text-slate-400"}`}>{l}</div>
          </div>
        ))}
      </div>

      {isTransfer && (
        <div data-testid="transfer-movement-card" className="mt-5 rounded-2xl border border-indigo-200 bg-indigo-50 p-4 text-xs text-indigo-900">
          <div className="text-[10px] uppercase tracking-widest text-indigo-500">Inter-branch transfer</div>
          <div className="mt-0.5 font-bold">{trip.customer_name} · driver {trip.kec_name}</div>
          <div className="mt-1">Security scans the <b>transfer gate pass</b> — no separate pass for these drives. Each ended drive syncs to Salesforce and counts for you and your branch. {trip.status === "closed" ? `Closed at the destination gate · ${trip.td_count || 0} drive(s) counted.` : "This record closes automatically when the car is gated in at the destination."}</div>
        </div>
      )}
      {gp && trip.status !== "closed" && (
        <div data-testid="gate-pass-card" className="mt-5 rounded-2xl bg-[#0E2240] p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#BF9B30]">QR Gate Pass</div>
              <div className="font-heading text-lg font-bold">{gp.reg_no}</div>
              <div className="text-xs text-slate-300">{gp.kec_name} · {gp.customer_name}</div>
              <div className="text-xs text-slate-300">{gp.td_type === "connected" ? `Connected Drive — ${(trip.customers || []).length} customers` : TYPE_LABEL[gp.td_type] || gp.td_type} · valid till {dt(gp.validity)}</div>
            </div>
            <div className="rounded-xl bg-white p-2.5">
              <QRCode value={gp.qr_token} size={110} />
            </div>
          </div>
          <div className="mt-3 text-[10px] text-slate-400">Show this at the security gate. Security scans out & in with odometer entry.</div>
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
        {[["Opening km", trip.opening_km], ["Closing km", trip.closing_km ?? "—"],
          ["Gate out", dt(trip.gate_out_ts)], ["Gate in", dt(trip.gate_in_ts)],
          ["Security out km", gp?.gate_out_odometer ?? "—"], ["Security in km", gp?.gate_in_odometer ?? "—"]].map(([l, v]) => (
          <div key={l} className="rounded-lg bg-white border border-slate-200 p-2.5">
            <div className="text-[10px] uppercase tracking-wider text-slate-400">{l}</div>
            <div className="font-semibold text-[#0E2240]">{v}</div>
          </div>
        ))}
      </div>
      {trip.closed_without_dl && (
        <div data-testid="closed-without-dl-flag" className="mt-3 rounded-xl bg-red-50 border border-red-200 p-3 text-xs font-bold text-[#BB162B]">⚠ Closed without driving licence for {(trip.dl_missing_customers || []).join(", ")} — variation reported to SM and above.</div>
      )}
      {trip.dl_missing_flag && !trip.closed_without_dl && (
        <div data-testid="dl-missing-flag" className="mt-3 rounded-xl bg-red-50 border border-red-200 p-3 text-xs font-bold text-[#BB162B]">⚠ Gated in without the customer's driving licence — SM alerted. Upload it now if you have it.</div>
      )}
      {trip.dl_pending && trip.status !== "closed" && ["sales", "sm", "admin"].includes(user.role) && <DLUpload trip={trip} onDone={load} />}
      {trip.status !== "closed" && ["sales", "sm", "admin"].includes(user.role) && (trip.type !== "utility" || ["marketing", "other"].includes(trip.utility_purpose)) && (user.role !== "sales" || trip.kec_roster_id === user.id || (trip.customers || []).some((c) => c.kec_roster_id === user.id)) && <AddDrive trip={trip} onDone={load} />}
      {(trip.status !== "closed" || isTransfer) && (trip.type === "connected" || (trip.additions || []).some((a) => a.status === "approved" || a.status === "completed")) && <Segments trip={trip} onDone={load} />}
      {(gp?.odo_mismatch_out || gp?.odo_mismatch_in) && (
        <div className="mt-3 rounded-xl bg-red-50 border border-red-200 p-3 text-xs font-bold text-[#BB162B]">⚠ Odometer mismatch flagged (KEC vs Security beyond ±2 km) — SM & TDC alerted</div>
      )}

      {!isTransfer && trip.status !== "closed" && ["sales", "sm", "tdc", "admin"].includes(user.role) && (trip.gate_out_ts && trip.gate_in_ts ? <CloseTrip trip={trip} onDone={() => nav("/trips")} /> : (
        <div data-testid="close-locked-note" className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600"><b className="text-[#0E2240]">Trip close is locked</b> — available only after Security records both gate-out and gate-in.{!trip.gate_out_ts && " If the car never leaves, cancel the booking instead or ask Admin to force-close with a reason."}</div>
      ))}

      {!isTransfer && user.role === "admin" && trip.status !== "closed" && <AdminForceClose trip={trip} onDone={() => nav("/trips")} />}

      {!isTransfer && user.role === "sm" && trip.status !== "closed" && (
        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <h3 className="text-sm font-bold text-amber-800">Manual Gate Override (SM)</h3>
          <p className="text-[11px] text-amber-700 mt-0.5">Use only if QR flow fails. Logged & reported to ZH weekly.</p>
          <input data-testid="override-odo-input" type="number" value={ovOdo} onChange={(e) => setOvOdo(e.target.value)} placeholder="Odometer km" className={inputCls} />
          <input data-testid="override-reason-input" value={ovReason} onChange={(e) => setOvReason(e.target.value)} placeholder="Reason" className={inputCls} />
          <div className="mt-2 flex gap-2">
            <button data-testid="override-out-btn" onClick={() => override("out")} className="flex-1 rounded-xl bg-amber-600 py-2.5 text-sm font-bold text-white">Override OUT</button>
            <button data-testid="override-in-btn" onClick={() => override("in")} className="flex-1 rounded-xl bg-amber-600 py-2.5 text-sm font-bold text-white">Override IN</button>
          </div>
        </div>
      )}
    </div>
  );
}
